import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { User, Mail, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user, isAuthenticated, openAuthModal, logout } = useAuth();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-brand-white flex flex-col items-center justify-center">
        <User className="h-16 w-16 text-brand-gold/50 mb-4" />
        <h1 className="font-heading text-3xl text-brand-black mb-4">{t('profile.title')}</h1>
        <p className="text-brand-dark/60 mb-6">Connectez-vous pour voir votre profil</p>
        <Button onClick={() => openAuthModal('login')} className="bg-brand-gold text-white hover:bg-brand-gold">
          {t('nav.login')}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 bg-brand-white" data-testid="profile-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="font-accent text-brand-gold text-sm tracking-[0.3em] uppercase">{t('profile.myAccount')}</span>
          <h1 className="font-heading text-4xl lg:text-5xl font-bold text-brand-black mt-2 mb-12">{t('profile.title')}</h1>
        </motion.div>

        <div className="space-y-6">
          {/* User Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-brand-cream border border-brand-gold/20 rounded-lg p-8"
          >
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 rounded-full bg-brand-gold/10 flex items-center justify-center">
                <User className="h-10 w-10 text-brand-gold" />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="font-heading text-2xl text-brand-black">{user.name}</h2>
                  {user.is_admin && (
                    <Badge className="bg-brand-gold/10 text-brand-gold border border-brand-gold/30">
                      <Shield className="h-3 w-3 mr-1" /> Admin
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2 text-brand-dark/60 mb-4">
                  <Mail className="h-4 w-4" />
                  <span>{user.email}</span>
                </div>

                <Badge className="bg-brand-cream border border-brand-gold/30 text-brand-dark/80">
                  {t('profile.retailAccount')}
                </Badge>
              </div>
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex gap-4"
          >
            <Button
              onClick={logout}
              variant="outline"
              className="border-brand-gold text-brand-gold hover:bg-brand-gold/10"
            >
              {t('profile.logout')}
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
